'use client'

import { createContext, useContext, useState, ReactNode } from 'react'
import { User } from '@/features/user/user.type'
import { ClientAuthUser } from '../models/auth-user'
import {
  logout as _logout,
  login as _login,
  register as _register,
} from '../actions/auth.actions'

type AuthContextType = {
  user: ClientAuthUser | null
  login: (phone: string, code: string) => Promise<User>
  register: (
    phone: string,
    firstName: string,
    lastName: string,
    email: string,
    timezone?: string,
    hiveId?: string,
    referral?: string
  ) => Promise<User>
  logout: () => void
  isAuthenticated: boolean
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children, user }: { children: ReactNode; user: ClientAuthUser | null }) {
  const login = async (phone: string, code: string) => {
    const result = await _login(phone, code)
    // Like register below: the action returns a wrong code as a typed result,
    // and this wrapper throws it so callers can show the message.
    if (!result.ok) {
      throw new Error(result.error)
    }
    return result.user
  }

  const register = async (
    phone: string,
    firstName: string,
    lastName: string,
    email: string,
    timezone?: string,
    hiveId?: string,
    referral?: string
  ) => {
    const result = await _register(phone, firstName, lastName, email, timezone, hiveId, referral)
    // The action now returns a typed result instead of throwing, so the opaque
    // "Server Components render" crash can't escape. Preserve this wrapper's
    // Promise<User> contract by throwing the typed error for callers to catch.
    if (!result.ok) {
      throw new Error(result.error)
    }
    return result.user
  }

  const logout = async () => {
    // The chat store wipe (#693): local chat data (IndexedDB) must never
    // outlive the session that ingested it (ADR-0054). Dynamic import so the
    // chat store's module graph only loads on this path; best-effort — a
    // storage failure must never block sign-out.
    try {
      const { wipeAllChatData } = await import('@/features/chat/store/wipe-chat-data')
      await wipeAllChatData()
    } catch {
      // Ignore: the server-side sign-out below is the part that must happen.
    }
    await _logout()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
